import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { getContactInfo } from '../lib/api'
import GlassCard from '../components/ui/GlassCard'
import ScrollReveal from '../components/ui/ScrollReveal'
import LoadingSpinner from '../components/ui/LoadingSpinner'
import Parallax from '../components/ui/Parallax'
import emailjs from '@emailjs/browser'
import type { ContactInfo } from '../types'

export default function Contact() {
  const [info, setInfo] = useState<ContactInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    company: '',
    service: '',
    message: '',
  })
  const [submitted, setSubmitted] = useState(false)
  const [sending, setSending] = useState(false)
  const [emailStatus, setEmailStatus] = useState('')

  useEffect(() => {
    getContactInfo().then((res) => {
      setInfo(res)
      setLoading(false)
    })
  }, [])

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    setFormState((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSending(true)
    try {
      const serviceId = import.meta.env.VITE_EMAILJS_SERVICE_ID
      const templateId = import.meta.env.VITE_EMAILJS_TEMPLATE_ID
      const publicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY

      if (!serviceId || !templateId || !publicKey) {
        console.warn('EmailJS not configured')
        fallbackMailto()
        return
      }

      const templateParams = {
        from_name: formState.name,
        from_email: formState.email,
        reply_to: formState.email,
        company: formState.company,
        service: formState.service,
        message: formState.message,
      }

      const result = await emailjs.send(serviceId, templateId, templateParams, publicKey)
      if (result.status === 200) {
        setEmailStatus('sent')
      }
    } catch (err) {
      console.error('EmailJS failed:', err)
      fallbackMailto()
      return
    } finally {
      setSending(false)
    }
    setSubmitted(true)
  }

  function fallbackMailto() {
    const subject = encodeURIComponent(`New Inquiry: ${formState.service || 'General'}`)
    const body = encodeURIComponent(
      `Name: ${formState.name}\nEmail: ${formState.email}\nCompany: ${formState.company}\nService: ${formState.service}\n\nMessage:\n${formState.message}`
    )
    window.open(`mailto:${info?.email || 'info@smartsupporttech.in'}?subject=${subject}&body=${body}`)
  }

  const resetForm = () => {
    setSubmitted(false)
    setEmailStatus('')
    setFormState({ name: '', email: '', company: '', service: '', message: '' })
  }

  if (loading) return <LoadingSpinner text="Loading..." />

  return (
    <main>
      {/* Page Header */}
      <section className="relative pt-36 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gray-950" />
        <Parallax speed={0.15} className="absolute top-0 right-0 w-[500px] h-[500px]">
          <div className="w-full h-full rounded-full bg-electric-blue/10 blur-[120px]" />
        </Parallax>
        <Parallax speed={0.12} className="absolute bottom-0 left-0 w-[400px] h-[400px]">
          <div className="w-full h-full rounded-full bg-electric-purple/10 blur-[120px]" />
        </Parallax>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block mb-4 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-electric-blue bg-electric-blue/10 rounded-full border border-electric-blue/20"
          >
            Get in Touch
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white"
          >
            Let's{' '}
            <span className="bg-gradient-to-r from-electric-blue to-electric-purple bg-clip-text text-transparent">
              Talk
            </span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-4 text-lg text-gray-400 max-w-xl mx-auto"
          >
            Have a project in mind? We'd love to hear about it. Drop us a
            message and we'll get back to you within 24 hours.
          </motion.p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Form */}
            <div className="lg:col-span-3">
              <ScrollReveal direction="left">
                <GlassCard className="p-8 md:p-10">
                  <>
                    <h2 className="text-2xl font-bold text-white mb-8">
                      Send Us a Message
                    </h2>
                    <form onSubmit={handleSubmit} className="space-y-5">
                      <div className="grid sm:grid-cols-2 gap-5">
                        <div>
                          <label
                            htmlFor="name"
                            className="block text-sm font-medium text-gray-400 mb-2"
                          >
                            Full Name *
                          </label>
                          <input
                            type="text"
                            id="name"
                            name="name"
                            required
                            value={formState.name}
                            onChange={handleChange}
                            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-electric-blue focus:ring-1 focus:ring-electric-blue outline-none transition-all"
                            placeholder="John Doe"
                          />
                        </div>
                        <div>
                          <label
                            htmlFor="email"
                            className="block text-sm font-medium text-gray-400 mb-2"
                          >
                            Email Address *
                          </label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            required
                            value={formState.email}
                            onChange={handleChange}
                            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-electric-blue focus:ring-1 focus:ring-electric-blue outline-none transition-all"
                            placeholder="john@example.com"
                          />
                        </div>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-5">
                        <div>
                          <label
                            htmlFor="company"
                            className="block text-sm font-medium text-gray-400 mb-2"
                          >
                            Company
                          </label>
                          <input
                            type="text"
                            id="company"
                            name="company"
                            value={formState.company}
                            onChange={handleChange}
                            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-electric-blue focus:ring-1 focus:ring-electric-blue outline-none transition-all"
                            placeholder="Acme Inc."
                          />
                        </div>
                        <div>
                          <label
                            htmlFor="service"
                            className="block text-sm font-medium text-gray-400 mb-2"
                          >
                            Service Interested In
                          </label>
                          <select
                            id="service"
                            name="service"
                            value={formState.service}
                            onChange={handleChange}
                            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-gray-400 focus:border-electric-blue focus:ring-1 focus:ring-electric-blue outline-none transition-all"
                          >
                            <option value="">Select a service</option>
                            <option value="web-design">Web Design</option>
                            <option value="development">
                              Web Development
                            </option>
                            <option value="ecommerce">eCommerce</option>
                            <option value="seo">SEO & Analytics</option>
                            <option value="branding">Digital Branding</option>
                            <option value="other">Other</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label
                          htmlFor="message"
                          className="block text-sm font-medium text-gray-400 mb-2"
                        >
                          Your Message *
                        </label>
                        <textarea
                          id="message"
                          name="message"
                          required
                          rows={5}
                          value={formState.message}
                          onChange={handleChange}
                          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-electric-blue focus:ring-1 focus:ring-electric-blue outline-none transition-all resize-none"
                          placeholder="Tell us about your project..."
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={sending}
                        className="w-full px-8 py-3.5 text-base font-semibold text-white rounded-xl bg-gradient-to-r from-electric-blue to-electric-purple hover:shadow-lg hover:shadow-electric-blue/25 transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {sending ? 'Sending...' : 'Send Message'}
                      </button>
                    </form>
                    <div className="mt-6 pt-6 border-t border-white/5">
                      <p className="text-sm text-gray-500 text-center mb-4">
                        Or reach us directly
                      </p>
                      <div className="flex gap-3 justify-center">
                        <a
                          href={`mailto:${info!.email}`}
                          className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-full bg-white/5 border border-white/10 hover:bg-electric-blue/20 hover:border-electric-blue/30 transition-all duration-300"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          {info!.email}
                        </a>
                        <a
                          href={info!.whatsapp}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-full bg-green-500/10 border border-green-500/20 hover:bg-green-500/20 hover:border-green-500/30 transition-all duration-300"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                          </svg>
                          WhatsApp
                        </a>
                      </div>
                    </div>
                  </>

                  <AnimatePresence>
                    {submitted && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
                        onClick={resetForm}
                      >
                        <motion.div
                          initial={{ opacity: 0, scale: 0.8, y: 40 }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.8, y: 40 }}
                          onClick={(e) => e.stopPropagation()}
                          className="bg-gray-900 border border-white/10 rounded-2xl p-8 max-w-md w-full text-center"
                        >
                          <div className={`text-5xl mb-4 ${emailStatus === 'failed' ? 'text-yellow-400' : ''}`}>
                            {emailStatus === 'sent' ? '🎉' : emailStatus.startsWith('failed') ? '⚠️' : '✅'}
                          </div>
                          <h3 className="text-2xl font-bold text-white mb-3">
                            Message Sent!
                          </h3>
                          <p className="text-gray-400 mb-2">
                            Thanks for reaching out! We'll review your message and
                            get back to you within 24 hours.
                          </p>
                          {emailStatus.startsWith('failed') && (
                            <p className="text-yellow-400 text-sm mb-6 bg-yellow-400/10 rounded-lg p-3 border border-yellow-400/20">
                              Your message was saved, but the email notification couldn't be sent. We'll still review it shortly.
                            </p>
                          )}
                          {emailStatus === 'not_configured' && (
                            <p className="text-yellow-400 text-sm mb-6 bg-yellow-400/10 rounded-lg p-3 border border-yellow-400/20">
                              Email notifications are not configured for this site.
                            </p>
                          )}
                          <button
                            onClick={resetForm}
                            className="px-6 py-3 bg-gradient-to-r from-electric-blue to-electric-purple text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-electric-blue/25 transition-all"
                          >
                            Send Another Message
                          </button>
                        </motion.div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </GlassCard>
              </ScrollReveal>
            </div>

            {/* Contact Info */}
            <div className="lg:col-span-2">
              <div className="space-y-6">
                <ScrollReveal direction="right" delay={0.1}>
                  <GlassCard className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-electric-blue/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-xl">📧</span>
                      </div>
                      <div>
                        <h3 className="text-white font-semibold mb-1">Email</h3>
                        <a
                          href={`mailto:${info!.email}`}
                          className="text-gray-400 hover:text-electric-blue transition-colors text-sm"
                        >
                          {info!.email}
                        </a>
                      </div>
                    </div>
                  </GlassCard>
                </ScrollReveal>

                <ScrollReveal direction="right" delay={0.2}>
                  <GlassCard className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-electric-blue/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-xl">📞</span>
                      </div>
                      <div>
                        <h3 className="text-white font-semibold mb-1">Phone</h3>
                        <a
                          href={`tel:${info!.phone}`}
                          className="text-gray-400 hover:text-electric-blue transition-colors text-sm"
                        >
                          {info!.phone}
                        </a>
                      </div>
                    </div>
                  </GlassCard>
                </ScrollReveal>

                <ScrollReveal direction="right" delay={0.3}>
                  <GlassCard className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-electric-blue/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-xl">💬</span>
                      </div>
                      <div>
                        <h3 className="text-white font-semibold mb-1">
                          WhatsApp
                        </h3>
                        <a
                          href={info!.whatsapp}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-400 hover:text-electric-blue transition-colors text-sm"
                        >
                          Chat with us
                        </a>
                      </div>
                    </div>
                  </GlassCard>
                </ScrollReveal>

                <ScrollReveal direction="right" delay={0.4}>
                  <GlassCard className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-electric-blue/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-xl">📍</span>
                      </div>
                      <div>
                        <h3 className="text-white font-semibold mb-1">
                          Location
                        </h3>
                        <p className="text-gray-400 text-sm leading-relaxed">
                          {info!.address}
                        </p>
                      </div>
                    </div>
                  </GlassCard>
                </ScrollReveal>

                <ScrollReveal direction="right" delay={0.5}>
                  <GlassCard className="p-6">
                    <h3 className="text-white font-semibold mb-4">
                      Follow Us
                    </h3>
                    <div className="flex gap-3">
                      {info!.socials.instagram && (
                        <a
                          href={info!.socials.instagram}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Instagram"
                          className="social-icon group"
                          style={{ '--glow': 'rgba(225, 48, 108, 0.6)' } as React.CSSProperties}
                        >
                          <div className="icon-container-sm" style={{ '--hover-bg': 'radial-gradient(circle at 30% 107%, #fdf497 0%, #fd5949 45%, #d6249f 60%, #285AEB 90%)' } as React.CSSProperties}>
                            <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-white">
                              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                            </svg>
                          </div>
                        </a>
                      )}
                      {info!.socials.linkedin && (
                        <a
                          href={info!.socials.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="LinkedIn"
                          className="social-icon group"
                          style={{ '--glow': 'rgba(0, 119, 181, 0.6)' } as React.CSSProperties}
                        >
                          <div className="icon-container-sm" style={{ '--hover-bg': '#0077b5' } as React.CSSProperties}>
                            <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-white">
                              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                            </svg>
                          </div>
                        </a>
                      )}
                       {info!.socials.twitter && (
                        <a
                          href={info!.socials.twitter}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Twitter"
                          className="social-icon group"
                          style={{ '--glow': 'rgba(29, 161, 242, 0.6)' } as React.CSSProperties}
                        >
                          <div className="icon-container-sm" style={{ '--hover-bg': '#1da1f2' } as React.CSSProperties}>
                            <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-white">
                              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                            </svg>
                          </div>
                        </a>
                      )}
                      {info!.socials.facebook && (
                        <a
                          href={info!.socials.facebook}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Facebook"
                          className="social-icon group"
                          style={{ '--glow': 'rgba(24, 119, 242, 0.6)' } as React.CSSProperties}
                        >
                          <div className="icon-container-sm" style={{ '--hover-bg': '#1877f2' } as React.CSSProperties}>
                            <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-white">
                              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                            </svg>
                          </div>
                        </a>
                      )}
                      {info!.socials.youtube && (
                        <a
                          href={info!.socials.youtube}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="YouTube"
                          className="social-icon group"
                          style={{ '--glow': 'rgba(255, 0, 0, 0.6)' } as React.CSSProperties}
                        >
                          <div className="icon-container-sm" style={{ '--hover-bg': '#ff0000' } as React.CSSProperties}>
                            <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-white">
                              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                            </svg>
                          </div>
                        </a>
                      )}
                    </div>
                  </GlassCard>
                  <style>{`
                    .icon-container-sm {
                      display: inline-flex;
                      width: 36px;
                      height: 36px;
                      border-radius: 50%;
                      align-items: center;
                      justify-content: center;
                      background: rgba(255, 255, 255, 0.05);
                      border: 1px solid rgba(255, 255, 255, 0.1);
                      transition: all 0.3s ease;
                    }
                    .social-icon:hover .icon-container-sm {
                      transform: translateY(-4px) scale(1.1);
                      background: var(--hover-bg);
                      box-shadow: 0 0 20px var(--glow, rgba(99,102,241,0.5));
                    }
                    .social-icon:hover svg {
                      animation: shake 0.5s;
                    }
                    @keyframes shake {
                      0%, 100% { transform: translateX(0) rotate(0); }
                      20% { transform: translateX(-3px) rotate(-5deg); }
                      40% { transform: translateX(3px) rotate(5deg); }
                      60% { transform: translateX(-3px) rotate(-5deg); }
                      80% { transform: translateX(3px) rotate(5deg); }
                    }
                  `}</style>
                </ScrollReveal>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  )
}
